import { transition, trigger, useAnimation } from '@angular/animations';
import { ActivatedRoute, Router } from '@angular/router';
import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { AccountType } from '@mnz-core/models/_enums';
import { IRootState } from '@mnz-core/modules/store/models/root-state';
import { select, Store } from '@ngrx/store';
import { fadeIn } from 'ng-animate';
import { combineLatestWith, first, map, switchMap, tap } from 'rxjs/operators';
import * as authActions from '@mnz-modules/login/store/actions/auth.actions';
import { APP_CONFIG } from '@mnz-core/injectors/app-configuration';
import { IAppConfiguration } from '@mnz-core/models/app-configuration';
import { firstValueFrom, Observable, of, Subject } from 'rxjs';
import * as authSelectors from '@mnz-modules/login/store/selectors/auth.selectors';
import { TranslateService } from '@ngx-translate/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { formUtils, getAppBaseUrl, isEmbedded } from '@mnz-shared/shared.constants';
import { createLoginForm } from '@mnz-modules/login/login.constants';
import { ISAMLRequest } from '@mnz-modules/login/models/saml-request';
import { ICredentials } from '@mnz-modules/login/models/credentials';

declare let grecaptcha: any;

@Component({
  selector: 'mnz-auth',
  templateUrl: 'saml.page.html',
  animations: [trigger('fadeIn', [transition('* => *', useAnimation(fadeIn, { params: { timing: 0.5 } }))])],
})
export class SamlPage implements OnInit {
  @ViewChild('credentialInput') credentialInput: ElementRef;
  @ViewChild('passwordInput') passwordInput: ElementRef;

  credentialErrorMessage$: Observable<string>;
  passwordErrorMessage$: Observable<string>;
  showPassword = false;

  private readonly _captcha = new Subject<string>();

  readonly captcha$ = this._captcha.asObservable();


  readonly emailErrorTranslation$ =  this._translate.get('LOGIN.INPUT_ERROR.INVALID_EMAIL');

  readonly emailRequiredTranslation$ =  this._translate.get('LOGIN.INPUT_ERROR.REQUIRED_EMAIL');

  readonly emailLengthTranslation$ = this._translate.get('LOGIN.INPUT_ERROR.LENGTH_EMAIL');

  constructor(
    @Inject(APP_CONFIG) private readonly _config: IAppConfiguration,
    private readonly _store$: Store<IRootState>,
    private readonly _router: Router,
    private readonly _translate: TranslateService,
    private readonly _formBuilder: FormBuilder,
    private readonly _route: ActivatedRoute
  ) {
    this.loadScript();
  }

  ngOnInit(): void {
    window['getResponceCapcha'] = this.getResponceCapcha.bind(this);

    this._store$
      .pipe(
        select(authSelectors.getPrefilledEmail),
        combineLatestWith(this.loginForm$),
        first(),
        tap(([email, form]) => {
          form.get('credential').setValue(email);
        })
      )
      .subscribe();
  }

  getResponceCapcha(captchaResponse: string) {
    this._captcha.next(captchaResponse);
  }

  readonly loginErrorTranslation$ = this._store$.pipe(
    select(authSelectors.getMessageFail),
    switchMap((message) => {
      return (message ? this._translate.get(message.body) : of(null))})
  );

  readonly loading$ = this._store$.pipe(select(authSelectors.getLoading));

  readonly prefilledEmail$ = this._store$.pipe(select(authSelectors.getPrefilledEmail));

  readonly accountType$ = of(AccountType.CARD_HOLDER);

  readonly loginForm$ = createLoginForm(this._formBuilder, this.accountType$);

  public togglePassWord(): void {
    this.showPassword = !this.showPassword;
  }

  backToOverview(): void {
    this._router.navigate(['/']);
  }

  async toRequestAccount(): Promise<void> {
    const accountType = await firstValueFrom(this.accountType$);
    this._router.navigate([`/account-activeren/${accountType}`]);
  }

  goToMoreInfo(): void {
    const { ns_business_url } = this._config.footerUrls;
    location.assign(ns_business_url);
  }

  onCloseMessage(): void {
    this._store$.dispatch(authActions.resetFailMessage());
  }

  readonly isEmailPrefilled$ = this.prefilledEmail$.pipe(
    map((email) => {
      return email && email.length > 0;
    })
  );

  readonly isEmailNotPrefilled$ = this.prefilledEmail$.pipe(
    map((email) => {
      return !email;
    })
  );

  public checkPassword(form) {
    const password = form.controls.password;
    const isTouchedDirtyAndInvalid = this.isTouchedDirtyAndInvalid(password);

    if (isTouchedDirtyAndInvalid && password.hasError('required')) {
      this.passwordErrorMessage$ = this._translate.get('LOGIN.INPUT_ERROR.REQUIRED_PASSWORD');
    } else {
      this.passwordErrorMessage$ = of('');
    }
  }

  async lostCredentials(form: FormGroup): Promise<void> {
    const accountType = await this.accountType$.pipe(first()).toPromise();
    const email = form.get('credential').value;

    let userInput: string;
    if (accountType === AccountType.CARD_HOLDER && email) {
      userInput = email;
    } else if (accountType === AccountType.CONTACT_PERSON) {
      userInput = '';
    }
    this._store$.dispatch(authActions.setUserInput({ userInput }));
    this._router.navigate([`/inlog-vergeten/${accountType}`]);
  }

  public redirectToNsGo(): void {
    location.assign(getAppBaseUrl(this._config.contextPath) + "/start-oauth2?nsgo=true&embedded=" + isEmbedded());
  }

  async login(form: FormGroup): Promise<void> {
    const { recaptchaEnabled } = this._config;

    /**
     * Workarround for password managers
     * Password managers will inject values into input fields which
     * do not trigger a change detection
     */
    form.get('credential').setValue(this.credentialInput.nativeElement.value.trim(), { emitEvent: false });
    form.get('password').setValue(this.passwordInput.nativeElement.value, { emitEvent: false });
    console.log(form);
    if (!form.valid) {
      if (form.get('credential').hasError('required') || form.get('credential').hasError('credential')) {
        this.checkCredentials(form);
        this.credentialInput.nativeElement.focus();
      } else if (form.get('password').hasError('required')) {
        this.checkPassword(form);
        this.passwordInput.nativeElement.focus();
      }
      return formUtils.touchControls(form.controls);
    }

    if (recaptchaEnabled) {
      grecaptcha.reset();
      grecaptcha.execute();
    }

    const samlRequest: ISAMLRequest = {
      samlRequest: this._route.snapshot.queryParamMap.get('SAMLRequest'),
      relayState: this._route.snapshot.queryParamMap.get('RelayState'),
      encodedSigAlg: btoa(this._route.snapshot.queryParamMap.get('SigAlg')),
      signature: this._route.snapshot.queryParamMap.get('Signature')
    };

    const credentials: ICredentials = {
      username: form.controls.credential.value.trim(),
      password: form.controls.password.value.trim()
    }

    const captcha = recaptchaEnabled ? await this.captcha$.pipe(first()).toPromise() : null;
    this._store$.dispatch(authActions.doSamlLogin({ samlRequest, credentials, captcha }));
  }

  public checkCredentials(form) {
    const credential = form.controls.credential;
    const isTouchedDirtyAndInvalid = this.isTouchedDirtyAndInvalid(credential);

    if (isTouchedDirtyAndInvalid) {
      if (credential.hasError('pattern') || (credential.hasError('credential') && !credential.hasError('required'))) {
        this.credentialErrorMessage$ = this.emailErrorTranslation$;
      } else if (credential.hasError('required')) {
        this.credentialErrorMessage$ = this.emailRequiredTranslation$;
      } else if (credential.hasError('maxlength') && !credential.hasError('pattern')) {
        this.credentialErrorMessage$ = this.emailLengthTranslation$;
      }
    } else {
      this.credentialErrorMessage$ = of('');
    }
  }

  private isTouchedDirtyAndInvalid(control) {
    return !control.valid && control.touched && control.dirty;
  }

  public onKeyDown(form) {
    this.login(form);
  }

  public loadScript() {
    const url = 'https://www.google.com/recaptcha/api.js';
    const node = document.createElement('script');
    node.src = url;
    node.async = true;
    node.defer = true;
    const head = document.getElementsByTagName('head')[0];
    const { recaptchaEnabled } = this._config;
    if (recaptchaEnabled) {
      head.appendChild(node);
    }
  }

  public isEmbedded(): boolean {
    return isEmbedded();
  }

  public isNsGoSsoEnabled (): boolean {
    return this._config.nsgo_sso_enabled;
  }
}
